import { Grid, IconButton } from "@mui/material";
import ContentTitle from "../../../../components/Content/ContentTitle";
import { INavigationLink } from "../../../../interfaces/INavigation";
import { useUserContext } from "../../../../contexts/UserContext";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import DefaultConstants from "../../../../data/Constants";
import { ClosedCaption, FilterAlt, Print, Refresh, Search as SearchIcon } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePartnerPmsContext } from "../../../../contexts/PartnerPmsContext";
import Filters from "../../../../components/PartnerPms/Filters";
import RoomCard from "../../../../components/PartnerPms/RoomCard";
import Legend from "../../../../components/PartnerPms/Legend";
import GovernanceDialog from "../../../../components/PartnerPms/Governance/GovernanceDialog";
import { IRoom } from "../../../../interfaces/IPartnerPms";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import Search from "../../../../components/Search";
import { useReportPreviewContext } from "../../../../contexts/ReportPreviewContext";
import Reports, { ReportsAlias } from "../../../../data/Reports";
import IReport from "../../../../interfaces/IReport";
import Tooltip from "../../../../components/Tooltip";
import { IRoomOverviewReportFilters } from "../Reports/RoomOverview/Filters";
import Empty from "../../../../components/Empty";
import useDrawer from "../../../../hooks/useDrawer";
import useSearch from "../../../../hooks/useSearch";
import StringHelper from "../../../../helpers/StringHelper";
import { GovernanceStatusData } from "../../../../data/Enums";


const RoomOverview = (linkProps: INavigationLink) => {
    const { environment } = useEnvironmentContext();
    const { translate } = useLanguageContext();
    const { enterprise, userPermissions } = useUserContext();
    const { 
        filteredRooms,
        handleRefreshRooms,
        selectedBlock,
        selectedFloor,
        selectedGovernanceStatus,
        selectedOccupancyStatus,
        selectedRoomStatus,
        selectedRoomType, 
        onlyUserRooms 
    } = usePartnerPmsContext();
    const { handleOpen } = useReportPreviewContext();

    const containerRef = useRef<HTMLDivElement | null>(null);

    const legendDrawer = useDrawer();
    const filterDrawer = useDrawer();
    
    const search = useSearch();

    const [roomsOnScreen, setRoomsOnScreen] = useState<number>(100); 
    const [selectedRoom, setSelectedRoom] = useState<IRoom | undefined>(undefined);
    

    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (selectedRoom === undefined) {
            const milliseconds = environment.partnerPms.dataRefreshTimeInSeconds * 1000;
            const interval = setInterval(() => {
                handleRefresh();
            }, milliseconds);
                
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line
    }, [selectedRoom]);


    useEffect(() => {
        if (containerRef.current) {
            const container = containerRef.current;
                
            container.onscroll = () => {
                const onContainerBottom = Math.abs(container.scrollHeight - container.clientHeight - container.scrollTop) < 10;
                if (onContainerBottom && roomsOnScreen < filteredRooms.length) {
                    setRoomsOnScreen(roomsOnScreen + 100);
                }
            }
        }

        // eslint-disable-next-line
    }, [filteredRooms, roomsOnScreen, containerRef]);


    const handleRefresh = useCallback(() => {
        handleRefreshRooms();
        // eslint-disable-next-line
    }, []);


    const handleOpenReportPreview = useCallback(() => {
        const report = Reports.find(r => r.alias === ReportsAlias.Governance.RoomOverview) as IReport;
        const filters: IRoomOverviewReportFilters = {
            enterprise: enterprise,
            occupancyStatus: selectedOccupancyStatus,
            governanceStatus: selectedGovernanceStatus,
            roomStatus: selectedRoomStatus,
            block: selectedBlock,
            floor: selectedFloor,
            roomType: selectedRoomType,
            onlyUserRooms: onlyUserRooms,
        };

        handleOpen(report, filters);
        // eslint-disable-next-line
    }, [enterprise, selectedOccupancyStatus, selectedGovernanceStatus, selectedRoomStatus, selectedBlock, selectedFloor, selectedRoomType, onlyUserRooms]);


    const roomCards = useMemo(() => {
        const allowedGovernanceStatus = Object.values(GovernanceStatusData)
            .filter(s => userPermissions.includes(`Governance.RoomOverview.View.${StringHelper.FirstLetterUpperCase(s.name)}`))
            .map(s => s.partnerId);

        let roomsAux = filteredRooms.filter(r => allowedGovernanceStatus.includes(r.governanceStatusId));

        if (search.value.trim().length > 0) {
            const value = StringHelper.Normalize(search.value.toUpperCase());
            roomsAux = roomsAux.filter(r => StringHelper.Normalize(r.description.toUpperCase()).includes(value));
        }
        
        return roomsAux.slice(0, roomsOnScreen).map(r => {
            return <Grid item lg={1.2} md={2.4} sm={4} xs={6} key={r.id}>
                <RoomCard
                    room={r}
                    showGovernanceTooltip={true}
                    key={r.id}
                    onClick={() => setSelectedRoom(r)}
                />
            </Grid>;
        });
    }, [userPermissions, filteredRooms, roomsOnScreen, search.value]);

    
    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle 
                        name={translate(linkProps.name)} 
                        description={enterprise?.name}
                        applyTranslation={false}
                    />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        userPermissions.includes(ReportsAlias.Governance.RoomOverview) && (
                            <Tooltip title={translate('print')}>
                                <IconButton onClick={handleOpenReportPreview}>
                                    <Print />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    <Tooltip title={translate('search')}>
                        <IconButton onClick={() => search.handleOpen()}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('legend')}>
                        <IconButton onClick={() => legendDrawer.handleOpen()}>
                            <ClosedCaption />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filterDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {
                roomCards.length > 0 ? (
                    <Grid 
                        container 
                        className='grid-content overflow-enabled justifycontent-flexstart aligncontent-flexstart' 
                        rowSpacing={DefaultConstants.gridRowSpacing} 
                        columnSpacing={DefaultConstants.gridColumnSpacing}
                        ref={containerRef}
                    >
                        {roomCards}
                    </Grid>
                ) : (
                    <Grid container className='grid-content'>
                        <Grid item xs={12} className='paper-content display-flex alignitems-center justifycontent-center background-transparent boxshadow-none'>
                            <Empty text='noRoomsFound' className="backgroundcolor-paper boxshadow-default" />
                        </Grid>
                    </Grid>
                )
            }

            <Search
                open={search.open}
                handleClose={() => search.handleClose()}
                value={search.value}
                handleChange={(e) => search.handleChangeValue(e.currentTarget.value)}
            />

            <Legend
                open={legendDrawer.open}
                handleClose={() => legendDrawer.handleClose()}
            />

            <Filters
                open={filterDrawer.open}
                handleClose={() => filterDrawer.handleClose()}
            />

            <GovernanceDialog
                open={selectedRoom !== undefined}
                handleClose={() => {
                    setSelectedRoom(undefined);
                    handleRefresh();
                }}
                roomId={selectedRoom?.id}
            />
        </>
    );
}

export default RoomOverview;